import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Boxes,
  Search,
  CheckCircle2,
  XCircle,
  Hash,
  Calendar,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

export default function VerifyPortal() {
  const { hash: paramHash } = useParams();
  const navigate = useNavigate();
  const [hash, setHash] = useState(paramHash || "");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);

  const verify = async (h) => {
    const value = (h ?? hash).trim();
    if (!value) return;
    setBusy(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/certificates/verify/${value}`);
      setResult(data);
    } catch (err) {
      setResult({ valid: false });
    } finally {
      setBusy(false);
    }
  };

  // Auto-verify on initial load if hash in URL
  if (paramHash && !searched && !busy) {
    verify(paramHash);
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="border-b border-foreground bg-grid">
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="mono mb-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            /public verification portal
          </div>
          <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tighter sm:text-5xl">
            Verify any Growthink credential.
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground/80">
            Paste a certificate hash below to confirm authenticity. No account, no fees — just provable, immutable trust.
          </p>

          <div className="mt-8 flex flex-col gap-2 sm:flex-row">
            <Input
              value={hash}
              onChange={(e) => setHash(e.target.value)}
              placeholder="0x9f3a7d1c4b8e6a2f5d9c1e8b4a7f2d6c..."
              className="h-12 rounded-none border-foreground font-mono"
              onKeyDown={(e) => e.key === "Enter" && verify()}
              data-testid="verify-input"
            />
            <Button
              size="lg"
              onClick={() => {
                if (hash) navigate(`/verify/${hash}`, { replace: true });
                verify();
              }}
              disabled={busy}
              className="h-12 rounded-none bg-foreground text-background hard-shadow"
              data-testid="verify-submit-btn"
            >
              <Search className="mr-2 h-4 w-4" />
              {busy ? "Verifying…" : "Verify hash"}
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 py-16">
        {!searched && (
          <div className="border-2 border-dashed border-foreground/30 p-10 text-center">
            <Hash className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              Enter a certificate hash above to verify on the blockchain.
            </p>
          </div>
        )}

        {searched && result && (
          result.valid ? <ValidResult cert={result.certificate} /> : <InvalidResult />
        )}
      </div>
      <Footer />
    </div>
  );
}

function ValidResult({ cert }) {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-2 border-accent bg-accent/10 p-5" data-testid="verify-valid-banner">
        <CheckCircle2 className="h-8 w-8 text-accent" />
        <div>
          <div className="font-display text-2xl font-extrabold text-foreground">
            Verified · Authentic
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            This credential is anchored on the Growthink network.
          </div>
        </div>
      </div>

      <div className="cert-grain border-2 border-foreground bg-white p-7 hard-shadow">
        <div className="flex items-start justify-between">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              CERTIFICATE OF COMPLETION · {cert.course_category}
            </div>
            <h2 className="font-display mt-2 text-3xl font-extrabold leading-tight tracking-tighter">
              {cert.course_title}
            </h2>
            <div className="mt-3 text-sm">
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Awarded to
              </div>
              <div className="font-semibold">{cert.student_name}</div>
            </div>
          </div>
          <div className="flex h-12 w-12 items-center justify-center bg-foreground text-background">
            <Boxes className="h-6 w-6" />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Issued
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4" />
              {new Date(cert.issued_at).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Network
            </div>
            <div className="mt-1 font-mono text-sm">{cert.network}</div>
          </div>
        </div>
        <div className="mt-6 space-y-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Certificate hash
            </div>
            <div className="mt-1 break-all border border-foreground/15 bg-muted px-2 py-1.5 font-mono text-[10px]">
              {cert.cert_hash}
            </div>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Transaction
            </div>
            <div className="mt-1 break-all border border-foreground/15 bg-muted px-2 py-1.5 font-mono text-[10px]">
              {cert.txn_hash}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Block
              </div>
              <div className="mt-1 font-mono text-xs">#{cert.block_number.toLocaleString()}</div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Status
              </div>
              <div className="mono mt-1 inline-flex items-center gap-1 bg-accent px-2 py-1 text-[10px] uppercase tracking-widest text-accent-foreground">
                <CheckCircle2 className="h-3 w-3" /> Valid
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InvalidResult() {
  return (
    <div className="border-2 border-destructive bg-destructive/10 p-8" data-testid="verify-invalid-banner">
      <div className="flex items-center gap-3">
        <XCircle className="h-8 w-8 text-destructive" />
        <div>
          <div className="font-display text-2xl font-extrabold text-foreground">
            Not Verified
          </div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            This hash does not match any record on the Growthink network.
          </div>
        </div>
      </div>
    </div>
  );
}
